import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, type Branch, type Currency, type LegalEntity } from '../../api.ts';
import { useMe } from '../../auth.tsx';
import { FormSection } from '../../components/FormModal.tsx';
import { MasterDeleteButton } from '../../components/MasterDelete.tsx';
import { DiscardBar, Modal } from '../../components/Modal.tsx';
import { TabPanel, Tabs, type TabItem } from '../../components/Tabs.tsx';
import { CountrySelect } from '../partners/common.tsx';
import {
  Badge,
  Button,
  ErrorBox,
  Field,
  Input,
  issueFor,
  Loading,
  Select,
} from '../../components/ui.tsx';
import {
  BranchFields,
  branchPayload,
  branchToForm,
  emptyBranchForm,
  type BranchForm,
} from '../Branches.tsx';
import {
  emptyLocationForm,
  LocationFields,
  locationToForm,
  saveLocation,
  type LocationForm,
  type Place,
  type Unit,
} from '../facilities/location-form.tsx';
import {
  CostCenterFields,
  costCenterToForm,
  saveCostCenter,
  type CostCenter,
  type CostCenterForm,
  type CostCenterOptions,
} from '../governance/cost-center-form.tsx';
import {
  EMPTY_ENTITY,
  entityPayload,
  entityTabs,
  entityToForm,
  isDirty,
  pickTab,
  type EntityForm,
  type EntityTab,
} from './record.ts';
import {
  BranchesTab,
  CostCentersTab,
  IntegrationTab,
  LocationsTab,
  RepresentativesTab,
  type RepresentativeRow,
  type Subsidiary,
} from './tabs.tsx';

const KINDS = ['holding', 'operating', 'other'] as const;

type Integration = { netsuiteCode: string; isElimination: boolean };

/** Formulário aberto por cima de uma aba (no lugar dela, nunca numa segunda janela). */
type Sub =
  | { kind: 'branch'; id: string | null; form: BranchForm; original: BranchForm }
  | { kind: 'location'; form: LocationForm; original: LocationForm }
  | { kind: 'costCenter'; id: string | null; form: CostCenterForm; original: CostCenterForm }
  | { kind: 'integration'; id: string; form: Integration; original: Integration };

const SUB_TAB: Record<Sub['kind'], EntityTab> = {
  branch: 'branches',
  location: 'locations',
  costCenter: 'costCenters',
  integration: 'integration',
};

/**
 * Ficha da Empresa em janela, com abas (Dados Mestres, guia visual: "Ficha com abas"). Cada aba
 * usa a permissão da tela própria do cadastro; criar ou alterar uma filial, um local ou um centro
 * de custo troca o conteúdo da janela pelo formulário daquele registro, com o próprio Salvar.
 */
export function EntityRecord({
  entityId,
  initialTab,
  readOnly,
  onClose,
  onCreated,
  onTabChange,
}: {
  /** null = nova empresa. */
  entityId: string | null;
  initialTab?: unknown;
  readOnly: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
  onTabChange?: (tab: EntityTab) => void;
}) {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const qc = useQueryClient();
  const access = entityTabs(me, readOnly);
  const visible = entityId ? access : access.filter((a) => a.id === 'details');
  const [tab, setTabState] = useState<EntityTab>(() => pickTab(visible, initialTab));
  const edit = (id: EntityTab) => visible.find((a) => a.id === id)?.edit ?? false;
  const shows = (id: EntityTab) => !!entityId && visible.some((a) => a.id === id);

  const entities = useQuery({
    queryKey: ['legal-entities'],
    queryFn: () => api.get<LegalEntity[]>('/core/legal-entities'),
  });
  const entity = entities.data?.find((e) => e.id === entityId);
  const [original, setOriginal] = useState<EntityForm>(() =>
    entity ? entityToForm(entity) : EMPTY_ENTITY,
  );
  const [form, setForm] = useState<EntityForm>(original);
  const [sub, setSub] = useState<Sub | null>(null);
  const [confirm, setConfirm] = useState<'close' | 'sub' | null>(null);
  const detailsDirty = isDirty(original, form);
  const subDirty = !!sub && isDirty(sub.original, sub.form);

  const currencies = useQuery({
    queryKey: ['currencies'],
    queryFn: () => api.get<Currency[]>('/core/currencies'),
    enabled: edit('details'),
  });
  const branches = useQuery({
    queryKey: ['branches', entityId ?? ''],
    queryFn: () =>
      api.get<Branch[]>(`/core/branches?legalEntityId=${encodeURIComponent(entityId ?? '')}`),
    enabled: shows('branches'),
  });
  const locations = useQuery({
    queryKey: ['locations', ''],
    queryFn: () => api.get<{ branches: Unit[]; locations: Place[] }>('/core/locations'),
    enabled: shows('locations'),
  });
  const costCenters = useQuery({
    queryKey: ['cost-centers'],
    queryFn: () => api.get<CostCenter[]>('/core/cost-centers'),
    enabled: shows('costCenters'),
  });
  const ccOptions = useQuery({
    queryKey: ['cost-centers', 'options'],
    queryFn: () => api.get<CostCenterOptions>('/core/cost-centers/options'),
    enabled: shows('costCenters') && edit('costCenters'),
  });
  const subsidiaries = useQuery({
    queryKey: ['structure', 'subsidiaries'],
    queryFn: () =>
      api.get<{ items: Subsidiary[]; actions: { update: boolean } }>('/structure/subsidiaries'),
    enabled: shows('integration'),
  });
  const representatives = useQuery({
    queryKey: ['representatives', `legalEntityId=${entityId ?? ''}`],
    queryFn: () =>
      api.get<RepresentativeRow[]>(
        `/core/representatives?legalEntityId=${encodeURIComponent(entityId ?? '')}`,
      ),
    enabled: shows('representatives'),
  });

  // Locais e centros de custo vêm do grupo inteiro; a ficha mostra só os desta empresa.
  const units = (locations.data?.branches ?? []).filter((u) => u.legalEntityCode === entity?.code);
  const unitIds = new Set(units.map((u) => u.id));
  const places = (locations.data?.locations ?? []).filter((l) => unitIds.has(l.branchId));
  const centers = (costCenters.data ?? []).filter((c) => c.legalEntityId === entityId);
  const subsidiary = subsidiaries.data?.items.find((s) => s.id === entityId);

  const saveDetails = useMutation({
    mutationFn: (f: EntityForm) =>
      entityId
        ? api.put<LegalEntity>(`/core/legal-entities/${entityId}`, entityPayload(f))
        : api.post<LegalEntity>('/core/legal-entities', entityPayload(f)),
    onSuccess: async (saved) => {
      const next = entityToForm(saved);
      setOriginal(next);
      setForm(next);
      await qc.invalidateQueries({ queryKey: ['legal-entities'] });
      await qc.invalidateQueries({ queryKey: ['structure'] });
      if (!entityId) onCreated(saved.id);
    },
  });
  const saveSub = useMutation({
    mutationFn: async (s: Sub) => {
      if (s.kind === 'branch')
        return s.id
          ? api.put(`/core/branches/${s.id}`, branchPayload(s.form))
          : api.post('/core/branches', branchPayload(s.form));
      if (s.kind === 'location') return saveLocation(s.form);
      if (s.kind === 'costCenter') return saveCostCenter({ id: s.id, form: s.form });
      return api.put(`/structure/subsidiaries/${s.id}`, {
        netsuiteCode: s.form.netsuiteCode || null,
        isElimination: s.form.isElimination,
      });
    },
    onSuccess: async (_saved, s) => {
      const key = {
        branch: ['branches'],
        location: ['locations'],
        costCenter: ['cost-centers'],
        integration: ['structure'],
      }[s.kind];
      await qc.invalidateQueries({ queryKey: key });
      setSub(null);
    },
  });

  const setTab = (next: EntityTab) => {
    setTabState(next);
    onTabChange?.(next);
  };
  const openSub = (next: Sub) => {
    saveSub.reset();
    setConfirm(null);
    setSub(next);
  };
  const leaveSub = () => {
    setConfirm(null);
    setSub(null);
    saveSub.reset();
  };
  const requestClose = () => (detailsDirty || subDirty ? setConfirm('close') : onClose());
  const requestLeaveSub = () => (subDirty ? setConfirm('sub') : leaveSub());

  if (entityId && !entity) {
    return (
      <Modal open size="record" onClose={onClose} title={t('legalEntities.title')}>
        {entities.isPending ? (
          <Loading />
        ) : (
          <ErrorBox error={entities.error ?? new Error('not_found')} />
        )}
      </Modal>
    );
  }

  const updateDetails = (patch: Partial<EntityForm>) => setForm((f) => ({ ...f, ...patch }));
  const updateSub = (patch: object) =>
    setSub((s) => (s ? ({ ...s, form: { ...s.form, ...patch } } as Sub) : s));

  const tabItems: TabItem<EntityTab>[] = visible.map((a) => ({
    id: a.id,
    label: t(`legalEntities.record.tabs.${a.id}`),
    count: {
      details: undefined,
      branches: branches.data?.length,
      locations: locations.data ? places.length : undefined,
      costCenters: costCenters.data ? centers.length : undefined,
      integration: undefined,
      representatives: representatives.data?.length,
    }[a.id],
  }));

  const subTitle = sub
    ? t(`legalEntities.record.sub.${sub.kind}.${'id' in sub && sub.id ? 'edit' : 'new'}`)
    : null;
  const title = sub ? subTitle : entity ? entity.legalName : t('legalEntities.new');
  const description = sub ? (
    <span>
      {entity?.code} · {entity?.legalName} › {t(`legalEntities.record.tabs.${SUB_TAB[sub.kind]}`)}
    </span>
  ) : entity ? (
    <span className="flex flex-wrap items-center gap-2">
      <span className="font-mono">{entity.code}</span>
      <span>
        · {t(`legalEntities.kinds.${entity.kind}`)} · {entity.country} ·{' '}
        <span className="font-mono">{entity.functionalCurrency}</span>
      </span>
      {entity.isActive ? (
        <Badge tone="ok">{t('fields.active')}</Badge>
      ) : (
        <Badge tone="off">{t('fields.inactive')}</Badge>
      )}
      <MasterDeleteButton table="core.legal_entity" id={entity.id} compact onDeleted={onClose} />
    </span>
  ) : (
    t('legalEntities.record.newHint')
  );

  const canSaveDetails = !sub && tab === 'details' && edit('details');
  const onSubmit = sub
    ? () => saveSub.mutate(sub)
    : canSaveDetails
      ? () => saveDetails.mutate(form)
      : undefined;

  const footerBar = confirm ? (
    <DiscardBar
      text={
        confirm === 'sub'
          ? t('legalEntities.record.discardSub')
          : detailsDirty
            ? t('legalEntities.record.discardDetails')
            : undefined
      }
      onKeep={() => setConfirm(null)}
      onDiscard={confirm === 'sub' ? leaveSub : onClose}
    />
  ) : undefined;

  const footer = sub ? (
    <>
      <span className="mr-auto basis-full self-center text-xs sm:basis-auto text-muted">
        {t('legalEntities.record.requiredLegend')}
      </span>
      <Button variant="secondary" onClick={requestLeaveSub}>
        {t('common.cancel')}
      </Button>
      <Button type="submit" disabled={saveSub.isPending}>
        {saveSub.isPending ? t('common.saving') : t(`legalEntities.record.sub.${sub.kind}.save`)}
      </Button>
    </>
  ) : canSaveDetails ? (
    <>
      <span className="mr-auto basis-full self-center text-xs sm:basis-auto text-muted">
        {t('legalEntities.record.requiredLegend')}
      </span>
      <Button variant="secondary" onClick={requestClose}>
        {t('common.cancel')}
      </Button>
      <Button type="submit" disabled={saveDetails.isPending || (!!entityId && !detailsDirty)}>
        {saveDetails.isPending
          ? t('common.saving')
          : entityId
            ? t('legalEntities.record.save')
            : t('legalEntities.record.create')}
      </Button>
    </>
  ) : (
    <>
      {detailsDirty && (
        <span className="mr-auto basis-full self-center text-xs sm:basis-auto text-danger">
          {t('legalEntities.record.unsavedDetails')}{' '}
          <button type="button" className="font-medium underline" onClick={() => setTab('details')}>
            {t('legalEntities.record.backToDetails')}
          </button>
        </span>
      )}
      <Button variant="secondary" onClick={requestClose}>
        {t('common.close')}
      </Button>
    </>
  );

  return (
    <Modal
      open
      size="record"
      compact
      onClose={onClose}
      title={title}
      description={description}
      dirty={detailsDirty || subDirty}
      discardText={detailsDirty ? t('legalEntities.record.discardDetails') : undefined}
      tabs={
        entityId && !sub && visible.length > 1 ? (
          <Tabs
            items={tabItems}
            value={tab}
            onChange={setTab}
            label={t('legalEntities.record.tabsLabel')}
            idPrefix="le"
          />
        ) : undefined
      }
      onSubmit={onSubmit}
      footer={footer}
      footerBar={footerBar}
    >
      {sub ? (
        <SubForm
          sub={sub}
          update={updateSub}
          error={saveSub.error}
          entity={entity}
          units={units}
          places={places}
          centers={costCenters.data ?? []}
          ccOptions={ccOptions.data}
        />
      ) : (
        <TabPanel idPrefix="le" id={tab} standalone={!entityId || visible.length <= 1}>
          {tab === 'details' && (
            <DetailsTab
              form={form}
              update={updateDetails}
              disabled={!edit('details')}
              error={saveDetails.error}
              entityId={entityId}
              entities={entities.data ?? []}
              currencies={currencies.data}
              saved={saveDetails.isSuccess && !detailsDirty}
            />
          )}
          {tab === 'branches' &&
            (branches.data ? (
              <BranchesTab
                branches={branches.data}
                canEdit={edit('branches')}
                onNew={() => {
                  const f = emptyBranchForm(entity);
                  openSub({ kind: 'branch', id: null, form: f, original: f });
                }}
                onEdit={(b) => {
                  const f = branchToForm(b);
                  openSub({ kind: 'branch', id: b.id, form: f, original: f });
                }}
              />
            ) : (
              <QueryState query={branches} />
            ))}
          {tab === 'locations' &&
            (locations.data ? (
              <LocationsTab
                units={units}
                locations={places}
                canEdit={edit('locations')}
                onNew={() => {
                  const f = emptyLocationForm(units[0]?.id ?? '');
                  openSub({ kind: 'location', form: f, original: f });
                }}
                onEdit={(l) => {
                  const f = locationToForm(l);
                  openSub({ kind: 'location', form: f, original: f });
                }}
              />
            ) : (
              <QueryState query={locations} />
            ))}
          {tab === 'costCenters' &&
            (costCenters.data ? (
              <CostCentersTab
                list={centers}
                canCreate={edit('costCenters') && !!ccOptions.data}
                canEdit={edit('costCenters') && !!ccOptions.data}
                onNew={() => {
                  const f = { ...costCenterToForm(), legalEntityId: entityId ?? '' };
                  openSub({ kind: 'costCenter', id: null, form: f, original: f });
                }}
                onEdit={(c) => {
                  const f = costCenterToForm(c);
                  openSub({ kind: 'costCenter', id: c.id, form: f, original: f });
                }}
              />
            ) : (
              <QueryState query={costCenters} />
            ))}
          {tab === 'integration' &&
            (subsidiaries.data ? (
              <IntegrationTab
                subsidiary={subsidiary}
                parentLabel={parentLabel(entities.data ?? [], entity)}
                canEdit={edit('integration') && subsidiaries.data.actions.update}
                onEdit={() => {
                  if (!subsidiary) return;
                  const f = {
                    netsuiteCode: subsidiary.netsuiteCode ?? '',
                    isElimination: subsidiary.isElimination,
                  };
                  openSub({ kind: 'integration', id: subsidiary.id, form: f, original: f });
                }}
              />
            ) : (
              <QueryState query={subsidiaries} />
            ))}
          {tab === 'representatives' &&
            (representatives.data ? (
              <RepresentativesTab rows={representatives.data} entityCode={entity?.code ?? ''} />
            ) : (
              <QueryState query={representatives} />
            ))}
        </TabPanel>
      )}
    </Modal>
  );
}

function QueryState({ query }: { query: { isError: boolean; error: unknown } }) {
  return query.isError ? <ErrorBox error={query.error} /> : <Loading />;
}

function parentLabel(list: LegalEntity[], entity: LegalEntity | undefined): string {
  const parent = list.find((e) => e.id === entity?.parentLegalEntityId);
  return parent ? `${parent.code} · ${Number(entity?.ownershipPct ?? 0)}%` : '';
}

function DetailsTab({
  form,
  update,
  disabled,
  error,
  entityId,
  entities,
  currencies,
  saved,
}: {
  form: EntityForm;
  update: (patch: Partial<EntityForm>) => void;
  disabled: boolean;
  error: unknown;
  entityId: string | null;
  entities: LegalEntity[];
  currencies: Currency[] | undefined;
  saved: boolean;
}) {
  const { t } = useTranslation();
  const text =
    (k: keyof EntityForm) => (ev: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      update({ [k]: ev.target.value });
  const err = (path: string) => issueFor(error, path);
  return (
    <fieldset disabled={disabled} className="flex min-w-0 flex-col gap-5">
      <ErrorBox error={error} showPaths={false} />
      {saved && (
        <p role="status" className="text-sm text-ok">
          {t('legalEntities.record.saved')}
        </p>
      )}
      <FormSection title={t('legalEntities.record.sections.identification')} columns={4}>
        <Field label={t('fields.code')} hint={t('fields.codeHint')} error={err('code')} required>
          <Input id="le-code" required value={form.code} onChange={text('code')} />
        </Field>
        <Field
          label={t('legalEntities.legalName')}
          error={err('legalName')}
          required
          className="sm:col-span-2"
        >
          <Input id="le-legal-name" required value={form.legalName} onChange={text('legalName')} />
        </Field>
        <Field label={t('legalEntities.kind')} required>
          <Select id="le-kind" value={form.kind} onChange={text('kind')}>
            {KINDS.map((k) => (
              <option key={k} value={k}>
                {t(`legalEntities.kinds.${k}`)}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label={t('legalEntities.tradeName')}
          error={err('tradeName')}
          className="sm:col-span-2"
        >
          <Input id="le-trade-name" value={form.tradeName} onChange={text('tradeName')} />
        </Field>
        <label className="flex items-center gap-2 self-end pb-2 text-sm">
          <input
            id="le-active"
            type="checkbox"
            checked={form.isActive}
            onChange={(ev) => update({ isActive: ev.target.checked })}
          />
          {t('fields.active')}
        </label>
      </FormSection>
      <FormSection title={t('legalEntities.record.sections.fiscal')} columns={4}>
        <Field label={t('fields.country')} error={err('country')} required>
          <CountrySelect
            id="le-country"
            value={form.country}
            placeholder={t('common.chooseCountry')}
            onChange={(country) => update({ country })}
          />
        </Field>
        <Field
          label={t('legalEntities.taxId')}
          hint={t('legalEntities.taxIdHint')}
          error={err('taxId')}
        >
          <Input id="le-tax-id" value={form.taxId} onChange={text('taxId')} />
        </Field>
        <Field
          label={t('legalEntities.taxRegime')}
          hint={t('legalEntities.taxRegimeHint')}
          error={err('taxRegime')}
        >
          <Input id="le-tax-regime" value={form.taxRegime} onChange={text('taxRegime')} />
        </Field>
        <Field label={t('fields.language')}>
          <Select id="le-locale" value={form.defaultLocale} onChange={text('defaultLocale')}>
            <option value="pt-BR">Português</option>
            <option value="en">English</option>
          </Select>
        </Field>
        <Field
          label={t('fields.timezone')}
          hint={t('fields.timezoneHint')}
          error={err('timezone')}
          required
          className="sm:col-span-2"
        >
          <Input id="le-timezone" required value={form.timezone} onChange={text('timezone')} />
        </Field>
      </FormSection>
      <FormSection title={t('legalEntities.record.sections.ownership')} columns={4}>
        <Field
          label={t('legalEntities.functionalCurrency')}
          error={err('functionalCurrency')}
          required
        >
          <Select
            id="le-currency"
            value={form.functionalCurrency}
            onChange={text('functionalCurrency')}
          >
            {(currencies ?? [{ code: form.functionalCurrency } as Currency]).map((c) => (
              <option key={c.code} value={c.code}>
                {c.code}
              </option>
            ))}
          </Select>
        </Field>
        <Field
          label={t('legalEntities.parent')}
          hint={t('legalEntities.parentHint')}
          error={err('parentLegalEntityId')}
          className="sm:col-span-2"
        >
          <Select
            id="le-parent"
            value={form.parentLegalEntityId}
            onChange={text('parentLegalEntityId')}
          >
            <option value="">{t('common.none')}</option>
            {entities
              .filter((e) => e.id !== entityId)
              .map((e) => (
                <option key={e.id} value={e.id}>
                  {e.code} · {e.legalName}
                </option>
              ))}
          </Select>
        </Field>
        <Field label={t('legalEntities.ownershipPct')} error={err('ownershipPct')}>
          <Input
            id="le-ownership"
            inputMode="decimal"
            disabled={!form.parentLegalEntityId}
            value={form.ownershipPct}
            onChange={text('ownershipPct')}
          />
        </Field>
      </FormSection>
    </fieldset>
  );
}

function SubForm({
  sub,
  update,
  error,
  entity,
  units,
  places,
  centers,
  ccOptions,
}: {
  sub: Sub;
  update: (patch: object) => void;
  error: unknown;
  entity: LegalEntity | undefined;
  units: Unit[];
  places: Place[];
  centers: CostCenter[];
  ccOptions: CostCenterOptions | undefined;
}) {
  const { t } = useTranslation();
  const grid = 'grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2 lg:grid-cols-3';
  return (
    <div className="flex flex-col gap-4">
      <ErrorBox error={error} showPaths={false} />
      {sub.kind === 'branch' && (
        <div className={grid}>
          <BranchFields
            form={sub.form}
            update={update}
            entities={entity ? [entity] : []}
            lockedEntity
            lockedHint={t('legalEntities.record.entityFromRecord')}
            error={error}
          />
        </div>
      )}
      {sub.kind === 'location' && (
        <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
          <LocationFields
            form={sub.form}
            setForm={(f) => update(f)}
            units={units}
            locations={places}
            error={error}
          />
          <p className="text-xs text-muted sm:col-span-2">
            {t('facilities.locations.inactiveHint')}
          </p>
        </div>
      )}
      {sub.kind === 'costCenter' &&
        (ccOptions ? (
          <div className={grid}>
            <CostCenterFields
              id={sub.id}
              form={sub.form}
              set={update}
              options={ccOptions}
              list={centers}
              error={error}
              lockedEntity
            />
          </div>
        ) : (
          <Loading />
        ))}
      {sub.kind === 'integration' && (
        <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
          <Field
            label={t('structure.fields.netsuiteCode')}
            hint={t('structure.netsuiteHint')}
            error={issueFor(error, 'netsuiteCode')}
          >
            <Input
              value={sub.form.netsuiteCode}
              onChange={(ev) => update({ netsuiteCode: ev.target.value })}
            />
          </Field>
          <label className="flex items-center gap-2 self-end pb-2 text-sm">
            <input
              type="checkbox"
              checked={sub.form.isElimination}
              onChange={(ev) => update({ isElimination: ev.target.checked })}
            />
            {t('structure.fields.elimination')}
          </label>
          <p className="text-xs text-muted sm:col-span-2">
            {t('legalEntities.record.notPayerText')}
          </p>
        </div>
      )}
    </div>
  );
}
